import React from 'react';
import {
  BathFixtureShell,
  Rect,
  Line,
  Curve,
  Circle,
} from './BathFixtureFrame';
import {
  BATHTUB_WALL_THICKNESS,
  bathtubFloorThickness,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Bathtub variants — dispatched on `obj.tubType` (canonical, see
 * `web-app/src/constants/glbConstants.js` TUB_TYPES + the
 * `shared-lib/tub-surround` module which reads `tubType` directly):
 *   - alcove (default): rectangular with deck/apron; reads as a tall
 *     box with rim line.
 *   - freestanding: oval-tub silhouette with no surround; sits clear
 *     of the floor on a tiny visible base.
 *   - drop-in: tub set into a deck — apron line + thin rim band.
 *   - undermount: similar to drop-in but rim is flush; thinner top
 *     band, no apron break.
 *   - clawfoot: freestanding silhouette + two visible feet at the
 *     bottom corners. Drawn when `obj.style === 'clawfoot'` (no
 *     canonical TUB_TYPES entry yet, but the plan calls it out).
 *
 * `tubType` is the same field `tub-surround/index.js` reads when
 * deciding whether to build a surround.
 */

export function resolveBathtubVariant(obj = {}) {
  if (obj.style === 'clawfoot' || obj.tubType === 'clawfoot') return 'clawfoot';
  const t = obj.tubType;
  if (t === 'freestanding' || t === 'drop-in' || t === 'undermount') return t;
  return 'alcove';
}

export function BathtubSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SideView
        view={view}
        depth={depthMeters}
        height={heightMeters}
        obj={obj}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <FrontView
      width={widthMeters}
      height={heightMeters}
      obj={obj}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

BathtubSchematic.supportsSideViews = true;

function FrontView({ width, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveBathtubVariant(obj);
  return (
    <BathFixtureShell width={width} height={height}>
      {variant === 'alcove' && (
        <Alcove
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'freestanding' && (
        <Freestanding
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'drop-in' && (
        <DropIn
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'undermount' && (
        <Undermount
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'clawfoot' && (
        <Clawfoot
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </BathFixtureShell>
  );
}

/**
 * Side elevation. Tubs are bilaterally symmetric in side profile (the
 * end you see from the left edge is the same shape as from the right),
 * so we draw once and the L/R `<g transform>` is identity for L and
 * mirrored for R. Drain shows on the front edge near the floor (front
 * of the bbox = right side in un-mirrored frame, left side after mirror
 * for R view). Spelling out: in the un-mirrored coordinate frame the
 * tub's back wall is at x=0 (where the faucet wall would be in alcove
 * installs); the apron / open side is at x=depth.
 */
function SideView({ view, depth, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveBathtubVariant(obj);
  const props = {
    depth,
    height,
    strokeColor,
    strokeWidth,
  };
  return (
    <BathFixtureShell width={depth} height={height}>
      <Rect
        x={0}
        y={0}
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <g
        transform={
          view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
        }
      >
        {variant === 'alcove' && <AlcoveSide {...props} />}
        {variant === 'freestanding' && <FreestandingSide {...props} />}
        {variant === 'drop-in' && <DropInSide {...props} />}
        {variant === 'undermount' && <UndermountSide {...props} />}
        {variant === 'clawfoot' && <ClawfootSide {...props} />}
      </g>
    </BathFixtureShell>
  );
}

/**
 * Section-style basin cavity: the concave interior that actually holds
 * water. Drawn as an open U — walls inset by the renderer's ~1.5\" shell
 * thickness dropping from the rim (`yTop`) to a rounded basin floor a
 * few inches above the shell bottom. Shared by every deck/apron variant
 * (alcove, drop-in, undermount) in both front and side views.
 */
function BasinCavity({ width, height, yTop = 0, strokeColor, strokeWidth }) {
  const IN = 0.0254;
  const wt = Math.min(BATHTUB_WALL_THICKNESS, width * 0.06);
  const floorT = bathtubFloorThickness(height);
  const bottom = height - floorT;
  // Paper-only bottom fillet for legibility; the mesh cavity is square.
  const r = Math.min(3 * IN, width / 2 - wt, (bottom - yTop) / 2);
  if (r <= 0) return null;
  const d = [
    `M ${wt} ${yTop}`,
    `L ${wt} ${bottom - r}`,
    `Q ${wt} ${bottom} ${wt + r} ${bottom}`,
    `L ${width - wt - r} ${bottom}`,
    `Q ${width - wt} ${bottom} ${width - wt} ${bottom - r}`,
    `L ${width - wt} ${yTop}`,
  ].join(' ');
  return <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />;
}

function DrainTick({ depth, height, strokeColor, strokeWidth }) {
  // Small drain mark on the front-bottom edge.
  const x = depth * 0.85;
  const y = height * 0.92;
  return (
    <Circle
      cx={x}
      cy={y}
      r={Math.min(depth, height) * 0.025}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/**
 * Alcove side: shell outline (frame rect from SideView) + basin cavity.
 */
function AlcoveSide({ depth, height, strokeColor, strokeWidth }) {
  return (
    <g>
      <BasinCavity
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <DrainTick
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Freestanding side: same oval silhouette as front (tubs are roughly
 * cylindrical/ellipsoidal), on a small base.
 */
function FreestandingSide({ depth, height, strokeColor, strokeWidth }) {
  const baseH = Math.min(height * 0.08, 0.04);
  const tubTopY = 0;
  const tubBottomY = height - baseH;
  const tubH = tubBottomY - tubTopY;
  const rx = depth / 2;
  const ry = tubH / 2;
  const d = [
    `M 0 ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 ${depth} ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 0 ${tubTopY + ry}`,
    'Z',
  ].join(' ');
  const baseW = depth * 0.4;
  const baseX = (depth - baseW) / 2;
  return (
    <g>
      <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />
      <Rect
        x={baseX}
        y={tubBottomY}
        width={baseW}
        height={baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function DropInSide({ depth, height, strokeColor, strokeWidth }) {
  const apronY = height * 0.18;
  return (
    <g>
      <Line
        x1={0}
        y1={apronY}
        x2={depth}
        y2={apronY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinCavity
        width={depth}
        height={height}
        yTop={apronY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <DrainTick
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function UndermountSide({ depth, height, strokeColor, strokeWidth }) {
  return (
    <g>
      <BasinCavity
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <DrainTick
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Clawfoot side: oval tub on two visible feet (front + back leg in
 * side elevation).
 */
function ClawfootSide({ depth, height, strokeColor, strokeWidth }) {
  const footR = Math.min(depth, height) * 0.05;
  const footCy = height - footR;
  const tubTopY = 0;
  const tubBottomY = height - footR * 2;
  const tubH = tubBottomY - tubTopY;
  const rx = depth / 2;
  const ry = tubH / 2;
  const d = [
    `M 0 ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 ${depth} ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 0 ${tubTopY + ry}`,
    'Z',
  ].join(' ');
  return (
    <g>
      <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />
      <Circle
        cx={footR * 2}
        cy={footCy}
        r={footR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Circle
        cx={depth - footR * 2}
        cy={footCy}
        r={footR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Alcove: shell rectangle with the basin cavity cut into the top.
 */
function Alcove({ width, height, strokeColor, strokeWidth }) {
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinCavity
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Freestanding: oval silhouette floating off the floor on a thin
 * pedestal/base. Side elevation reads as a wide ellipse.
 */
function Freestanding({ width, height, strokeColor, strokeWidth }) {
  const baseH = Math.min(height * 0.08, 0.04);
  const tubTopY = 0;
  const tubBottomY = height - baseH;
  const tubH = tubBottomY - tubTopY;
  const cx = width / 2;
  const rx = width / 2;
  const ry = tubH / 2;
  const d = [
    `M 0 ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 ${width} ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 0 ${tubTopY + ry}`,
    'Z',
  ].join(' ');
  const baseW = width * 0.4;
  const baseX = (width - baseW) / 2;
  return (
    <g>
      <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />
      <Rect
        x={baseX}
        y={tubBottomY}
        width={baseW}
        height={baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Drop-in: tub set into a deck; the deck/apron extends to the floor
 * but the visible top section is a flush band, not a separate rim.
 */
function DropIn({ width, height, strokeColor, strokeWidth }) {
  const apronY = height * 0.18;
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={0}
        y1={apronY}
        x2={width}
        y2={apronY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinCavity
        width={width}
        height={height}
        yTop={apronY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Undermount: tub hangs from below a continuous deck surface.
 * Elevation reads as a single flat-topped rectangle with no rim band
 * (rim is concealed under the countertop deck above the tub).
 */
function Undermount({ width, height, strokeColor, strokeWidth }) {
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinCavity
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Clawfoot: freestanding silhouette + four small feet at the corners
 * (two visible in side elevation). The "claw" detail is drawn as
 * small circles at each bottom corner.
 */
function Clawfoot({ width, height, strokeColor, strokeWidth }) {
  const footR = Math.min(width, height) * 0.04;
  const footCy = height - footR;
  const tubTopY = 0;
  const tubBottomY = height - footR * 2;
  const tubH = tubBottomY - tubTopY;
  const rx = width / 2;
  const ry = tubH / 2;
  const d = [
    `M 0 ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 ${width} ${tubTopY + ry}`,
    `A ${rx} ${ry} 0 0 1 0 ${tubTopY + ry}`,
    'Z',
  ].join(' ');
  return (
    <g>
      <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />
      <Circle
        cx={footR * 2}
        cy={footCy}
        r={footR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Circle
        cx={width - footR * 2}
        cy={footCy}
        r={footR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
